import React, {useCallback, useEffect, useRef, useState} from 'react';
import {BaseFullPage} from '../base';
import {BankIDView} from '../bankid/BankIDView';
import {useBankIDStatus} from '../../hooks/useBankIDStatus';
import useAuthState from '../../hooks/useAuthState';
import {isRestErrorWithStatus, useParamState} from '../../hooks';
import {config} from '../../config';
import {getOrCreateBft, BftResult} from '../../services/bft';
import {WrongBrowserError} from '../error/WrongBrowserError';
import {useBankIDComplete} from '../../hooks/useBankIDComplete';
import {LoginBanner} from './LoginBanner';

export const LoginMBIDPage = () => {
  const [{id, token: urlAutoStartToken = '', provider, type}] = useParamState<{
    id?: string;
    token?: string;
    provider?: string;
    type?: string;
  }>();
  const bankIdHost =
    provider === 'mock' ? `${config.bankIdHost}/mock` : config.bankIdHost;
  const authType = type === 'direct' ? 'direct' : 'oauth';

  const auth = useAuthState();
  const entrypointTokenMutation = auth.getEntrypointAccessToken;
  // The entrypoint token's lifetime (2 min) is shorter than the BankID QR
  // window (5 min), so a login left open re-mints it via this rather than
  // relying on the one fetched at mount.
  const refreshEntrypointToken = useCallback(async () => {
    const result = await entrypointTokenMutation.mutateAsync();
    return result.accessToken;
  }, [entrypointTokenMutation]);
  const requestId = id!;
  const [bftState, setBftState] = useState<BftResult | undefined>();
  const [isSessionInitialized, setIsSessionInitialized] = useState(false);
  // Legacy mode: URL has ?token=. New mode (DIDS toggle on): /init response
  // carries it. Persist for refresh in new mode (where the init marker
  // correctly skips a second /init call).
  const [resolvedAutoStartToken, setResolvedAutoStartToken] = useState<string>(
    () =>
      urlAutoStartToken ||
      localStorage.getItem(`autoStartToken-${requestId}`) ||
      '',
  );
  // Sticky: once the status poll starts including a QR code (backend QR-rotation
  // toggle on), keep using it - including across the rare tick where it's
  // momentarily absent - rather than falling back to the legacy /qr poll and
  // reintroducing the stale-QR race that merging qrCode into status fixes.
  const [lastQrCode, setLastQrCode] = useState<string | undefined>(undefined);
  const bft = bftState?.bft.value;
  const browserFixationRequestedRef = useRef(false);
  const initCalledForRef = useRef<string | null>(null);
  const bankIDStatus = useBankIDStatus({
    token: requestId,
    authToken: entrypointTokenMutation.data?.accessToken,
    bft,
    statusEnabled: isSessionInitialized,
    host: bankIdHost,
    refreshAuthToken: refreshEntrypointToken,
  });

  const {initializeSessionAsync} = bankIDStatus;
  const status = bankIDStatus.query.data?.status;
  const authId = bankIDStatus.query.data?.authId;
  // Prefer the status poll's autoStartToken so a rotated order still opens (see SigningMBIDPage).
  const autoStartToken =
    bankIDStatus.query.data?.autoStartToken ?? resolvedAutoStartToken;
  const expiresAt = bankIDStatus.query.data?.expiresAt;
  if (
    bankIDStatus.query.data?.qrCode &&
    bankIDStatus.query.data.qrCode !== lastQrCode
  ) {
    setLastQrCode(bankIDStatus.query.data.qrCode); // eslint-disable-line react-hooks/set-state-in-render
  }
  const qrCode = bankIDStatus.query.data?.qrCode ?? lastQrCode;
  const accessToken = entrypointTokenMutation.data?.accessToken;
  const {complete: bankIDComplete} = useBankIDComplete({
    token: requestId,
    authToken: accessToken,
    bft,
    host: bankIdHost,
    refreshAuthToken: refreshEntrypointToken,
  });

  // A 401 on the status poll, a 400 on /init, or a browser-fixation/complete
  // error means the user was redirected to a different browser (e.g. webview → system browser).
  const queryError = bankIDStatus.query.error;
  const initError = bankIDStatus.initializeSession.error;
  const isWrongBrowserError =
    isRestErrorWithStatus(queryError, [401]) ||
    isRestErrorWithStatus(initError, [400]) ||
    !!(bankIDStatus.createBrowserFixation.error ?? bankIDComplete.error);

  // A 409 on cancel means the authentication already completed server-side
  // before the cancel request arrived (the user clicked Cancel in the brief
  // window after returning from the BankID app but before the next status
  // poll). Ignore it — the status poll above will pick up the terminal
  // status and redirect normally, so surfacing it as an error would
  // needlessly interrupt an otherwise-successful login.
  const isCancelRaceError = isRestErrorWithStatus(
    bankIDStatus.cancel.error,
    [409],
  );

  const error =
    (!isWrongBrowserError ? bankIDStatus.query.error : null) ??
    (!isWrongBrowserError ? bankIDStatus.initializeSession.error : null) ??
    (!isCancelRaceError ? bankIDStatus.cancel.error : null);
  if (error) throw error;

  const cleanupLocalStorage = useCallback(() => {
    localStorage.removeItem(`bft-${requestId}`);
    localStorage.removeItem(`bankid-init-${requestId}`);
    localStorage.removeItem(`autoStartToken-${requestId}`);
  }, [requestId]);

  useEffect(() => {
    if (!entrypointTokenMutation.data) return;

    const shouldRedirectToCanceled =
      status === 'UNKNOWN' ||
      status === 'EXPIRED' ||
      status === 'CANCELLED' ||
      status === 'FAILED';

    if (status === 'COMPLETED') {
      if (authType === 'direct') {
        const completeAndRedirect = async () => {
          if (!bft || browserFixationRequestedRef.current) return;
          browserFixationRequestedRef.current = true;
          const redirectUrl = await bankIDComplete.mutateAsync();
          cleanupLocalStorage();
          window.location.replace(redirectUrl);
        };
        void completeAndRedirect();
      } else {
        const createFixationAndRedirect = async () => {
          if (!bft || browserFixationRequestedRef.current) return;
          browserFixationRequestedRef.current = true;
          const response =
            await bankIDStatus.createBrowserFixation.mutateAsync(requestId);
          cleanupLocalStorage();
          window.location.replace(
            auth.getPreauthorizationURL(
              response.authId,
              'completed',
              response.bpt,
            ),
          );
        };
        void createFixationAndRedirect();
      }
    } else if (shouldRedirectToCanceled) {
      cleanupLocalStorage();
      if (authId) {
        if (authType === 'direct') {
          const cancelAndRedirect = async () => {
            const redirectUrl = await bankIDComplete.mutateAsync();
            window.location.replace(redirectUrl);
          };
          void cancelAndRedirect();
        } else {
          window.location.replace(
            auth.getPreauthorizationURL(authId, 'canceled', undefined, status),
          );
        }
      }
    }
  }, [
    entrypointTokenMutation.data,
    status,
    authType,
    requestId,
    bftState,
    auth,
    bft,
    authId,
    bankIDComplete,
    bankIDStatus.createBrowserFixation,
    cleanupLocalStorage,
  ]);

  useEffect(() => {
    if (!entrypointTokenMutation.data && !entrypointTokenMutation.isPending) {
      entrypointTokenMutation.mutate(undefined);
    }
  }, [entrypointTokenMutation]);

  useEffect(() => {
    setBftState(getOrCreateBft(requestId)); // eslint-disable-line react-hooks/set-state-in-effect
    setIsSessionInitialized(false); // eslint-disable-line react-hooks/set-state-in-effect
    setLastQrCode(undefined); // eslint-disable-line react-hooks/set-state-in-effect
    initCalledForRef.current = null;
  }, [requestId]);

  useEffect(() => {
    if (!bftState || !accessToken) return;

    if (isSessionInitialized) return;

    const initMarkerKey = `bankid-init-${requestId}`;
    const hasInitMarker =
      localStorage.getItem(initMarkerKey) === bftState.bft.value;

    if (hasInitMarker) {
      setIsSessionInitialized(true); // eslint-disable-line react-hooks/set-state-in-effect
      return;
    }

    if (initCalledForRef.current === requestId) return;
    initCalledForRef.current = requestId;

    (async () => {
      try {
        const result = await initializeSessionAsync();
        // New mode: take the autoStartToken from the response when the URL
        // didn't carry one. Legacy mode is unaffected.
        if (initCalledForRef.current !== requestId) return;
        if (!urlAutoStartToken && result?.autoStartToken) {
          localStorage.setItem(
            `autoStartToken-${requestId}`,
            result.autoStartToken,
          );
          setResolvedAutoStartToken(result.autoStartToken);
        }
        localStorage.setItem(initMarkerKey, bftState.bft.value);
        setIsSessionInitialized(true);
      } catch {
        if (initCalledForRef.current === requestId) {
          initCalledForRef.current = null;
        }
      }
    })();
  }, [
    accessToken,
    bftState,
    isSessionInitialized,
    requestId,
    initializeSessionAsync,
    urlAutoStartToken,
  ]);

  if (isWrongBrowserError) {
    return <WrongBrowserError />;
  }

  return (
    <BaseFullPage id={'login_mbid'} header={<LoginBanner />}>
      <BankIDView
        type={'auth'}
        token={requestId}
        status={status}
        autoStartToken={autoStartToken}
        expiresAt={expiresAt}
        qrCode={qrCode}
        authToken={accessToken}
        bft={bft}
        host={bankIdHost}
        useReturnUrl={!urlAutoStartToken}
        refreshAuthToken={refreshEntrypointToken}
        onCancel={async () => {
          if (bankIDStatus.cancel.isPending) return;
          try {
            await bankIDStatus.cancel.mutateAsync();
          } catch (err) {
            // Already completed server-side — the status poll will redirect.
            if (!isRestErrorWithStatus(err, [409])) throw err;
          }
        }}
      />
    </BaseFullPage>
  );
};
